import { useEffect, useState } from "react";
import { api, fmtDateTime } from "../lib/api";
import { Coins, Minus, Plus } from "lucide-react";

const KIND_LABELS = {
  consult: "Consultazione effettuata",
  purchase: "Acquisto crediti",
  grant: "Crediti assegnati",
  expire: "Crediti scaduti",
  adjustment: "Variazione",
  sub_gift: "Abbonamento regalato",
  sub_revoke: "Abbonamento annullato",
  sub_activate: "Abbonamento attivato",
  sub_renew: "Rinnovo abbonamento",
};

export default function CreditHistory({ alwaysOpen = false }) {
  const [open, setOpen] = useState(alwaysOpen);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoadError(false);
    api.get("/credits/history").then((r) => setData(r.data)).catch(() => setLoadError(true));
  };

  useEffect(() => {
    if ((alwaysOpen || open) && data === null && !loadError) load();
  }, [open, alwaysOpen, data, loadError]);

  const content = loadError ? (
    <div className="mt-4" data-testid="credit-history-error">
      <p className="text-sm text-purple-100/85 mb-3">Impossibile caricare il saldo.</p>
      <button onClick={load} data-testid="credit-history-retry"
        className="text-xs text-purple-300/60 hover:text-amber-300 border border-[#4a2175] rounded-full px-4 py-2 transition-colors">
        Riprova
      </button>
    </div>
  ) : data === null ? (
    <p className="text-xs text-purple-300/50 mt-4">Caricamento…</p>
  ) : (
    <div className="mt-4">
      <p className="text-sm mb-4" data-testid="credit-balance">
        Saldo attuale: <span className="text-amber-200 font-semibold">{data.balance} crediti</span>
      </p>
      {data.purchased !== undefined && (
        <div className="mb-4 space-y-1" data-testid="credits-breakdown">
          <p className="text-xs text-purple-300/60">
            di cui acquistati: <span className="text-purple-100/85">{data.purchased}</span>
            {(data.gift_credits || []).length > 0 && (
              <> · regalati: <span className="text-amber-200">{(data.gift_credits || []).reduce((s, g) => s + g.remaining, 0)}</span></>
            )}
          </p>
          {(data.gift_credits || []).map((g, i) => (
            <p key={i} className="text-xs text-purple-300/60" data-testid={`gift-credit-${i}`}>
              {g.remaining} {g.remaining === 1 ? "credito regalato" : "crediti regalati"} · scade il {fmtDateTime(g.expires_at)}
            </p>
          ))}
        </div>
      )}
      {data.movements.length === 0 ? (
        <p className="text-xs text-purple-300/50" data-testid="credit-history-empty">Nessun movimento registrato finora.</p>
      ) : (
        <div className="space-y-2" data-testid="credit-history-list">
          {data.movements.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border border-[#4a2175]/60 rounded-lg px-3 py-2.5" data-testid={`credit-movement-${m.id}`}>
              <span className={`inline-flex items-center gap-1 font-semibold ${m.delta < 0 ? "text-rose-300" : "text-emerald-300"}`}>
                {m.delta < 0 ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                {Math.abs(m.delta)}
              </span>
              <span className="text-purple-100/85">{m.note || KIND_LABELS[m.kind] || m.kind}</span>
              <span className="ml-auto text-purple-300/50">
                {fmtDateTime(m.at)} · saldo {m.balance_after}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className={alwaysOpen ? "" : "mb-10"} data-testid="credit-history-section">
      {!alwaysOpen && (
        <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400 flex items-center gap-2 mb-5">
          <Coins className="w-4 h-4" /> Storico crediti
        </h2>
      )}
      <div className="mystic-card p-6">
        {!alwaysOpen && (
          <button onClick={() => setOpen((o) => !o)} data-testid="credit-history-toggle" className="w-full flex items-center justify-between">
            <span className="text-sm text-purple-100/85">Movimenti dei tuoi crediti consultazione</span>
            <span className="text-xs text-purple-300/50">{open ? "Nascondi" : "Mostra"}</span>
          </button>
        )}
        {(alwaysOpen || open) && content}
      </div>
    </div>
  );
}
